import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { CircleCheckIcon, InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { authErrorMessage, signIn } from "../../shared/auth";
import { openAfterAccountChange } from "../../shared/auth/web-address";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PasswordInput } from "./PasswordInput";

export interface SignInPageProps {
  /**
   * Present only on the first run after upgrading over device-paired local
   * state. Rendered as an informational notice, never an error —
   * nothing has gone wrong and nothing the user cares about was lost.
   */
  readonly legacyStateNotice?: { readonly pairingLinks: number; readonly sessions: number };
  /** The account's password was just reset, which signed it out everywhere. */
  readonly passwordChanged?: boolean;
}

/**
 * Dovod-account email/password sign-in.
 */
export function SignInPage({ legacyStateNotice, passwordChanged }: SignInPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const { error } = await signIn.email(input);
      if (error) {
        throw new Error(authErrorMessage(error));
      }
    },
    onSuccess: () => openAfterAccountChange("/", (to) => void navigate({ to })),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutation.mutate({ email, password });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("auth/signInToYour_nt2qrr")}</CardTitle>
        </CardHeader>
        <CardContent>
          {legacyStateNotice ? (
            <Alert variant="info" className="mb-4">
              <InfoIcon />
              <AlertDescription>{t("auth/yourPreviousDevicePairing_aa44sl")}</AlertDescription>
            </Alert>
          ) : null}
          {passwordChanged ? (
            <Alert variant="success" className="mb-4">
              <CircleCheckIcon />
              <AlertDescription>{t("auth/yourPasswordHasBeen_kfzj7f")}</AlertDescription>
            </Alert>
          ) : null}
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sign-in-email">{t("auth/emailAddress_1m22un")}</Label>
              <Input
                id="sign-in-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <Label htmlFor="sign-in-password">{t("auth/password_dn5241")}</Label>
                <Link
                  className="text-primary text-sm underline-offset-4 hover:underline"
                  to="/forgot-password"
                >
                  {t("auth/forgotYourPassword_ws302f")}
                </Link>
              </div>
              <PasswordInput
                id="sign-in-password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            {mutation.error instanceof Error ? (
              <p className="text-destructive text-sm" role="alert">
                {mutation.error.message}
              </p>
            ) : null}
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("auth/signingIn_gxau9a") : t("auth/signIn_hq86wm")}
            </Button>
            <p className="text-muted-foreground text-sm">
              {t("auth/haveAnInvite_l6rapj")}{" "}
              <Link className="text-primary underline-offset-4" to="/register">
                {t("auth/register_ugub6a")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
